// Considered: MessageAtom — rejected because it renders one message and the preview has three states (working /
// result / failure) with a disclosure and a diagnostics list; it still carries the failure state. Considered:
// UndoStripMolecule — rejected because its contract is "a change already made"; this is a change not yet made.
// See spec Reuse Audit.
import React, { useId, useState } from 'react';
import { useTranslation } from 'localization';
import type { AggregationMethod, RollupWeighting, StatusValue } from 'types/spherePoints';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import MessageAtom from '../../atoms/Message/MessageAtom';
import { getLocalizedStatusLabel } from '../../../utils/statusColors';
import type { RollupSimulateResponse } from '../../../types/statusFactors';
import { fromStored, type RollupWins } from '../RelatedSpheresEditor/combineModel';
import { diagnosticSentences, type DiagnosticSentenceContext } from '../RelatedSpheresEditor/diagnosticSentences';
import type { RollupSimulationState } from './useRollupSimulation';
import './RollupChangePreview.scss';

export interface RollupChangePreviewProps {
  state: RollupSimulationState;
  result: RollupSimulateResponse | null;
  onRetry: () => void;
  /** The unsaved combine axes; the comparison lists the other methods under this weighting. */
  aggregationMethod: AggregationMethod;
  weighting: RollupWeighting;
  diagnosticContext?: DiagnosticSentenceContext;
  className?: string;
}

const WINS: RollupWins[] = ['worst', 'balanced', 'majority'];

const methodKey = (wins: RollupWins, weighting: RollupWeighting): AggregationMethod => {
  if (wins === 'balanced') return weighting === 'influence' ? 'weighted' : 'average';
  return wins;
};

const RollupChangePreview: React.FC<RollupChangePreviewProps> = ({
  state,
  result,
  onRetry,
  aggregationMethod,
  weighting,
  diagnosticContext = {},
  className,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const regionId = useId();
  if (state === 'idle' || state === 'hidden') return null;

  const statusLabel = (status: StatusValue) => getLocalizedStatusLabel(status, t as (key: string) => string);
  const classes = ['rollup-change-preview', `rollup-change-preview--${state}`, className].filter(Boolean).join(' ');

  if (state === 'failed') {
    return (
      <section className={classes} aria-label={t('statusFactors.preview.heading', 'Change preview')} data-testid="rollup-change-preview">
        <MessageAtom
          severity="warn"
          className="rollup-change-preview__failure-message"
          text={t(
            'statusFactors.preview.failure',
            "Couldn't preview this change because the preview service didn't answer — the result is still unknown until you save."
          )}
        />
        <div className="rollup-change-preview__failure-actions">
          <ButtonAtom
            type="button"
            variant="text"
            size="small"
            label={t('statusFactors.preview.tryAgain', 'Try again')}
            onClick={onRetry}
          />
          <span className="rollup-change-preview__muted">
            {t('statusFactors.preview.savingUnaffected', 'Saving is unaffected.')}
          </span>
        </div>
      </section>
    );
  }

  const isWorking = state === 'working' || !result;
  let headline: string;
  if (isWorking) {
    headline = t('statusFactors.preview.working', 'Working out what this change would do…');
  } else if (result.baseline.status === result.status) {
    headline = t('statusFactors.preview.unchanged', 'This change would leave this factor on {{status}}.', {
      status: statusLabel(result.status),
    });
  } else {
    headline = t('statusFactors.preview.moved', 'This change would move this factor from {{from}} → {{to}}.', {
      from: statusLabel(result.baseline.status),
      to: statusLabel(result.status),
    });
  }

  const candidateWins = fromStored({ aggregationMethod, weighting }).wins;
  const rows =
    state === 'result' && result?.byMethod
      ? WINS.filter((wins) => wins !== candidateWins).flatMap((wins) => {
          const outcome = result.byMethod?.[methodKey(wins, weighting)];
          return outcome ? [{ wins, status: outcome.status }] : [];
        })
      : [];
  const sentences = state === 'result' && result ? diagnosticSentences(result.diagnostics, diagnosticContext, t) : [];

  return (
    <section className={classes} aria-label={t('statusFactors.preview.heading', 'Change preview')} data-testid="rollup-change-preview">
      <p className="rollup-change-preview__headline" role="status">
        <i
          className={`rollup-change-preview__glyph ${isWorking ? 'pi pi-spinner' : 'pi pi-info-circle'}`}
          aria-hidden="true"
        />
        <span>{headline}</span>
      </p>
      {sentences.length > 0 && (
        <ul className="rollup-change-preview__diagnostics">
          {sentences.map((sentence) => (
            <li key={sentence.code}>{sentence.text}</li>
          ))}
        </ul>
      )}
      {state === 'result' && (
        <div className="rollup-change-preview__actions">
          {rows.length > 0 && (
            <ButtonAtom
              type="button"
              variant="text"
              size="small"
              label={
                expanded
                  ? t('statusFactors.preview.hideCompare', 'Hide other methods')
                  : t('statusFactors.preview.compare', 'Compare other methods')
              }
              aria-expanded={expanded}
              aria-controls={regionId}
              onClick={() => setExpanded((open) => !open)}
            />
          )}
          <ButtonAtom
            type="button"
            variant="text"
            size="small"
            label={t('statusFactors.preview.recheck', 'Recheck')}
            onClick={onRetry}
          />
        </div>
      )}
      <ul
        id={regionId}
        className="rollup-change-preview__methods"
        hidden={!(expanded && rows.length > 0)}
      >
        {rows.map((row) => (
          <li key={row.wins} data-testid={`rollup-preview-method-${row.wins}`}>
            <span className="rollup-change-preview__dot" data-status={row.status} aria-hidden="true" />
            <span className="rollup-change-preview__method">{t(`statusFactors.combine.wins.${row.wins}.label`)}</span>
            <span className="rollup-change-preview__muted">
              {row.status === result?.status
                ? t('statusFactors.preview.compareAgree', 'would agree · {{status}}', { status: statusLabel(row.status) })
                : t('statusFactors.preview.compareDiffer', 'would land on {{status}}', {
                    status: statusLabel(row.status),
                  })}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};

RollupChangePreview.displayName = 'RollupChangePreview';

export default RollupChangePreview;
